<template>
    <v-card flat>
        <v-overlay :value="loadingExportInvoiceData">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-toolbar color="bal-gray" dense>
            <v-toolbar-title>{{ activeExportInvoice.id }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
                    v-if="
                    !activeExportInvoice.is_active &&
                        hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.EDIT)
                "
                    color="bal-red"
                    dark
                    @click="editInvoice"
            >
                <v-icon>edit</v-icon>
                {{ "Edit" | t }}
            </v-btn>
            <v-btn
                    v-else-if="
                    activeExportInvoice.is_active &&
                        hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.EDIT)
                "
                    color="bal-red"
                    dark
                    @click="unlockInvoice"
            >
                <v-icon>lock_open</v-icon>
                {{ "Unlock" | t }}
            </v-btn>
            <v-menu
                    bottom
                    left
                    offset-y
                    v-if="
                    !activeExportInvoice.is_active &&
                        (hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.EDIT) ||
                            hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.DELETE))
                "
            >
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="px-2 ml-2 mx-2" color="bal-dark-gray" dark style="min-width: 0;">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item
                            @click="handleUpdatePricesClick"
                            v-if="hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.EDIT)"
                    >
                        <v-list-item-title>{{ "Update prices" | t }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                            @click="handleDeleteClick"
                            v-if="hasPermissionForAction(PermissionsConstants.EXPORT, PermissionsConstants.DELETE)"
                    >
                        <v-list-item-title style="color: red">{{ "Delete" | t }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn icon @click="closeDrawer()">
                <v-icon>close</v-icon>
            </v-btn>
        </v-toolbar>
        <v-tabs v-model="tab" background-color="bal-gray" color="bal-red">
            <v-tab style="text-transform: none !important;">
                {{ "General info" | t }}
            </v-tab>
            <v-tab-item>
                <general-info-tab/>
                <v-divider class="t-divider"/>
                <rows-tab/>
                <stickers-sum-view/>
                <v-divider class="t-divider" v-if="activeClientChart.length > 0"/>
                <chart-view v-if="activeClientChart.length > 0"/>
            </v-tab-item>
            <v-tab style="text-transform: none !important;">
                {{ "Comments" | t }}
                <v-chip
                        class="ml-1 pa-2"
                        outlined
                        x-small
                        color="#f41823"
                        v-if="activeExportInvoice.comments && activeExportInvoice.comments.length > 0"
                >
                    {{ activeExportInvoice.comments.length }}
                </v-chip>
            </v-tab>
            <v-tab-item>
                <comment-tab/>
            </v-tab-item>
            <v-tab style="text-transform: none !important;">
                {{ "Files" | t }}
                <v-chip
                        class="ml-1 pa-2"
                        outlined
                        x-small
                        color="#f41823"
                        v-if="activeExportInvoice.invoice_files_data && activeExportInvoice.invoice_files_data.length > 0"
                >
                    {{ activeExportInvoice.invoice_files_data.length }}
                </v-chip>
            </v-tab>
            <v-tab-item>
                <file-tab/>
            </v-tab-item>
            <v-tab style="text-transform: none !important;">
                {{ "Transport" | t }}
                <v-chip
                        class="ml-1 pa-2"
                        outlined
                        x-small
                        color="#f41823"
                        v-if="activeExportInvoice.transports && activeExportInvoice.transports.length > 0"
                >
                    {{ activeExportInvoice.transports.length }}
                </v-chip>
            </v-tab>
            <v-tab-item>
                <transport-tab/>
            </v-tab-item>
        </v-tabs>
    </v-card>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import GeneralInfoTab from "./export-sale-invoice-tab-items/general-info-tab";
import RowsTab from "./export-sale-invoice-tab-items/rows-tab";

import Store from "../../store";
import ChartView from "./export-sale-invoice-tab-items/chart-view";
import StickersSumView from "./export-sale-invoice-tab-items/stickers-sum-view";
import CommentTab from "./export-sale-invoice-tab-items/comment-tab";
import FileTab from "./export-sale-invoice-tab-items/file-tab";
import { PermissionsConstants } from "@/permissions";
import TransportTab from "./export-sale-invoice-tab-items/transport-tab";

export default {
    name: "export-sale-invoice-view",
    components: { FileTab, CommentTab, StickersSumView, ChartView, GeneralInfoTab, RowsTab, TransportTab },
    data() {
        return {
            tab: 0,
            tabs: ["General info", "Comments", "Files", "Transport"]
        };
    },
    mounted() {
        document.addEventListener("keydown", this.nextTab);
        Store.dispatch("ExportSaleInvoiceStore/getExportInvoiceData", this.$route.params.exportInvoiceId);
        if (this.$route.params.tab) {
            this.tab = this.tabs.indexOf(this.$route.params.tab);
        }
    },
    destroyed() {
        document.removeEventListener("keydown", this.nextTab);
    },
    watch: {
        "activeExportInvoice.invoice_date": {
            handler(invoiceDate) {
                if (invoiceDate) {
                    const parts = invoiceDate.split(".");
                    if (parts.length === 3) {
                        const scrollToMonth = `${parts[2]}-${parts[1]}`;
                        if (this.$route.query.scrollToMonth !== scrollToMonth) {
                            this.$router.replace({
                                query: { ...this.$route.query, scrollToMonth }
                            }).catch(() => {});
                        }
                    }
                }
            },
            immediate: true
        }
    },
    computed: {
        ...mapState("ExportSaleInvoiceStore", [
            "activeExportInvoice",
            "loadingExportInvoiceData",
            "tableOptions",
            "activeClientChart"
        ]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        tabName() {
            return this.tabs[this.tab];
        }
    },
    methods: {
        closeDrawer() {
            this.$router.push({ name: "export-sale-invoice-list" });
        },
        editInvoice() {
            this.$router.push({
                name: "export-sale-invoice-edit",
                params: { exportInvoiceId: this.$route.params.exportInvoiceId, tab: this.tabs[this.tab] }
            });
        },
        async unlockInvoice() {
            if (confirm(this.t("Are you sure you want to unlock this invoice?"))) {
                let id = this.activeExportInvoice.id;
                await Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                    prop: "is_active",
                    value: false
                });
                await Store.dispatch("ExportSaleInvoiceStore/sendExportInvoice", this.activeExportInvoice);
                await this.$router.push({
                    name: "export-sale-invoice-edit",
                    params: { exportInvoiceId: id }
                });
            }
        },
        nextTab(e) {
            if (e.keyCode === 69 && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                this.activeExportInvoice.is_active ? this.unlockInvoice() : this.editInvoice();
            }
            if (document.activeElement.tagName !== "TEXTAREA") {
                if (e.key === "ArrowLeft") {
                    if (this.tab > 0) {
                        this.tab -= 1;
                    }
                } else if (e.key === "ArrowRight") {
                    if (this.tab + 1 < this.tabs.length) {
                        this.tab += 1;
                    }
                }
            }
        },
        async handleDeleteClick() {
            if (confirm(this.t("Are you sure you want to delete this invoice?"))) {
                await Store.dispatch("ExportSaleInvoiceStore/deleteInvoice", {
                    invoice_id: this.activeExportInvoice.id
                });
                await Store.commit("ExportSaleInvoiceStore/setActiveExportInvoice", {});
                return this.$router.push({ name: "export-sale-invoice-list" });
            }
        },
        async handleUpdatePricesClick() {
            if (confirm(this.t("Are you sure you want to update this invoice prices?"))) {
                await Store.dispatch("ExportSaleInvoiceStore/updateInvoicePrices", {
                    invoice_id: this.activeExportInvoice.id
                });
                await Store.dispatch("ExportSaleInvoiceStore/getExportInvoiceData", this.activeExportInvoice.id);
            }
        }
    }
};
</script>

<style scoped>
.t-divider {
    border-color: darkgrey !important;
}
</style>
